import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { t as tr } from "@/lib/i18n";

const AppCtx = createContext(null);

export function AppProvider({ children }) {
    const [lang, setLangState] = useState(
        () => localStorage.getItem("legashield_lang") || "de",
    );
    const [user, setUser] = useState(null);
    const [authState, setAuthState] = useState("checking"); // checking | in | out
    const [masterPassword, setMasterPassword] = useState(null); // never persisted

    const setLang = (l) => {
        localStorage.setItem("legashield_lang", l);
        setLangState(l);
    };
    const t = useCallback((p) => tr(lang, p), [lang]);

    const checkAuth = useCallback(async () => {
        // If OAuth callback fragment present, skip — AuthCallback handles it.
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setAuthState("checking");
            return;
        }
        try {
            const r = await api.get("/auth/me");
            setUser(r.data);
            setAuthState("in");
        } catch {
            setUser(null);
            setAuthState("out");
        }
    }, []);

    useEffect(() => {
        checkAuth();
    }, [checkAuth]);

    const login = async (email, password) => {
        const r = await api.post("/auth/login", { email, password });
        localStorage.setItem("legashield_token", r.data.token);
        setUser(r.data.user);
        setAuthState("in");
        return r.data.user;
    };

    const register = async (payload) => {
        const r = await api.post("/auth/register", payload);
        localStorage.setItem("legashield_token", r.data.token);
        setUser(r.data.user);
        setAuthState("in");
        return r.data.user;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        localStorage.removeItem("legashield_token");
        setUser(null);
        setMasterPassword(null);
        setAuthState("out");
    };

    return (
        <AppCtx.Provider
            value={{
                lang, setLang, t,
                user, setUser,
                authState, setAuthState,
                checkAuth, login, register, logout,
                masterPassword, setMasterPassword,
            }}
        >
            {children}
        </AppCtx.Provider>
    );
}

export function useApp() {
    return useContext(AppCtx);
}
